import { Component } from '@angular/core';
import { BaseCellRendererComponent } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { SyllabusStoreModel } from '@examdojo/category/v2';
import { Icon } from '@examdojo/core/icon';
import { ICellRendererParams } from 'ag-grid-community';
import { map } from 'rxjs';
import { ProgramBadgeComponent, ProgramBadgeConfig, ProgramData } from '../../shared/program-badge.component';

interface ArrayParamValue<T> {
  type: T;
  count: number;
}

function isArrayParamValue<T>(value: unknown): value is ArrayParamValue<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'count' in value &&
    typeof (value as { count: unknown }).count === 'number'
  );
}

export interface ProgramBadgeCellRendererParams extends ICellRendererParams {
  config?: ProgramBadgeConfig;
}

@Component({
  selector: 'exam-cms-program-badge-cell-renderer',
  template: `
    @if (state.programs.length === 0) {
      <span class="text-sm text-gray-500">None</span>
    } @else {
      <div class="flex flex-wrap gap-1">
        @for (program of state.programs; track program.code) {
          <exam-cms-program-badge [program]="program" [config]="state.config" />
        }
      </div>
    }
  `,
  standalone: true,
  imports: [ProgramBadgeComponent],
})
export class ProgramBadgeCellRendererComponent extends BaseCellRendererComponent<
  Array<SyllabusStoreModel | string>,
  unknown,
  ProgramBadgeCellRendererParams
> {
  constructor() {
    super();
  }

  readonly state = connectState({
    programs: this.value$.pipe(
      map((valueParam) => {
        if (!valueParam) {
          return [];
        }

        // Handle ParamValue type - extract the actual array
        let arrayData: Array<SyllabusStoreModel | string>;
        if (isArrayParamValue<Array<SyllabusStoreModel | string>>(valueParam)) {
          arrayData = valueParam.type;
        } else if (Array.isArray(valueParam)) {
          arrayData = valueParam as Array<SyllabusStoreModel | string>;
        } else {
          return [];
        }

        // Check if this is the special "All" case for ADMIN users
        if (arrayData.length === 1 && arrayData[0] === 'All') {
          return [
            {
              code: 'All',
              short_name: 'All Programs',
              full_name: 'All Programs',
              icon: null,
            },
          ];
        }

        // Handle normal syllabus array - filter out any string values
        const syllabuses = arrayData.filter(
          (item): item is SyllabusStoreModel => typeof item === 'object' && item !== null,
        ) as SyllabusStoreModel[];

        if (!syllabuses?.length) {
          return [];
        }

        // Extract unique programs from syllabuses
        const uniquePrograms = new Map<string, ProgramData>();

        syllabuses.forEach((syllabus) => {
          if (syllabus.program) {
            uniquePrograms.set(syllabus.program.code, {
              code: syllabus.program.code,
              short_name: syllabus.program.short_name,
              full_name: syllabus.program.full_name,
              icon: syllabus.program.icon as Icon | null,
            });
          }
        });

        return Array.from(uniquePrograms.values());
      }),
    ),
    config: this.params$.pipe(map((params) => ({ size: 'sm' as const, showIcon: true, ...params.config }))),
  });
}
